import React, { useMemo } from 'react';
import { formatDateTime } from '../formatting';
import { shortenWalletAddress } from '../../../utils/commonFunction';
import {
    computeCompanyDashboardMetrics,
    computeEmployeeDashboardMetrics,
} from '../utils/workspaceDashboardMetrics';
import EscrowFundDistribution from './EscrowFundDistribution';
import NeedsAttentionPanel from './NeedsAttentionPanel';

/** One panel with multiple stats inside — avoids a grid of separate boxes. */
const SummaryPanel = ({ title, subtitle, children }) => (
    <div className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm dark:border-zinc-800 dark:bg-zinc-950/50">
        {title ? (
            <div className="mb-4 border-b border-zinc-100 pb-3 dark:border-zinc-800">
                <h3 className="text-sm font-semibold text-zinc-900 dark:text-white">{title}</h3>
                {subtitle ? (
                    <p className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-400">{subtitle}</p>
                ) : null}
            </div>
        ) : null}
        {children}
    </div>
);

const StatCell = ({ label, value, hint }) => (
    <div className="min-w-0 px-1 py-2 sm:py-0">
        <p className="text-[11px] font-medium uppercase tracking-wide text-zinc-500 dark:text-zinc-500">
            {label}
        </p>
        <p className="mt-1 text-xl font-bold tabular-nums text-zinc-900 dark:text-white sm:text-2xl">
            {value}
        </p>
        {hint ? <p className="mt-0.5 text-[11px] text-zinc-500 dark:text-zinc-500">{hint}</p> : null}
    </div>
);

function activityIcon(type = '') {
    const t = String(type).toLowerCase();
    if (t.includes('dispute')) {
        return { icon: 'fa-solid fa-triangle-exclamation', tone: 'text-rose-600 dark:text-rose-400' };
    }
    if (t.includes('rework') || t.includes('resubmit')) {
        return { icon: 'fa-solid fa-rotate', tone: 'text-amber-700 dark:text-amber-300' };
    }
    if (t.includes('submit')) {
        return { icon: 'fa-regular fa-paper-plane', tone: 'text-sky-600 dark:text-sky-400' };
    }
    if (t.includes('approv') || t.includes('complete')) {
        return { icon: 'fa-solid fa-circle-check', tone: 'text-emerald-600 dark:text-emerald-400' };
    }
    if (t.includes('assign')) {
        return { icon: 'fa-regular fa-clipboard', tone: 'text-teal-700 dark:text-teal-400' };
    }
    return { icon: 'fa-regular fa-bell', tone: 'text-zinc-500 dark:text-zinc-400' };
}

const RecentActivity = ({ notifications, onViewAll }) => (
    <div className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm dark:border-zinc-800 dark:bg-zinc-950/50">
        <div className="flex items-center justify-between gap-2">
            <h3 className="text-sm font-semibold text-zinc-900 dark:text-white">Recent activity</h3>
            {onViewAll ? (
                <button
                    type="button"
                    onClick={onViewAll}
                    className="text-xs font-semibold text-teal-700 hover:text-teal-800 dark:text-teal-400 dark:hover:text-teal-300"
                >
                    View all
                </button>
            ) : null}
        </div>
        {notifications.length ? (
            <ul className="mt-3 space-y-2.5">
                {notifications.slice(0, 5).map((n, index) => {
                    const { icon, tone } = activityIcon(
                        `${n.type || ''} ${n.title || ''} ${n.body || ''}`,
                    );
                    const ts = n.createdAt || n.updatedAt;
                    return (
                        <li
                            key={n.id || `${ts}-${index}`}
                            className="flex gap-3 border-b border-zinc-100 pb-2.5 last:border-0 last:pb-0 dark:border-zinc-800/80"
                        >
                            <span
                                className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-zinc-200 bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-900 ${tone}`}
                            >
                                <i className={`${icon} text-[13px]`} />
                            </span>
                            <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                                    {n.title || n.message || 'Workspace event'}
                                </p>
                                <time className="mt-0.5 block text-[11px] text-zinc-500">
                                    {formatDateTime(ts)}
                                </time>
                            </div>
                        </li>
                    );
                })}
            </ul>
        ) : (
            <p className="mt-4 text-sm text-zinc-500 dark:text-zinc-400">
                No activity yet.
            </p>
        )}
    </div>
);

const JobsWorkspaceDashboard = ({
    role,
    company,
    employeeCompany,
    walletAddress,
    tokenSymbol,
    formatNumber,
    notifications,
    stats,
    companyTasks,
    employees,
    employeeTasks,
    openCompanyJobRoute,
    onCreateTask,
    onOpenTask,
    payrollCard = null,
}) => {
    const displayName =
        role === 'company'
            ? company?.name || 'Your company'
            : employeeCompany?.name || 'Your workspace';

    const companyMetrics = useMemo(
        () => computeCompanyDashboardMetrics(companyTasks, employees),
        [companyTasks, employees],
    );

    const employeeMetrics = useMemo(
        () => computeEmployeeDashboardMetrics(employeeTasks, stats),
        [employeeTasks, stats],
    );

    const walletLabel = shortenWalletAddress(walletAddress);

    return (
        <div className="space-y-5">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h2 className="text-2xl font-bold text-zinc-900 dark:text-white">{displayName}</h2>
                    <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">
                        {role === 'company' ? 'Company workspace' : 'Contributor workspace'}
                        {walletLabel ? (
                            <span className="ml-2 font-mono text-xs text-zinc-500">{walletLabel}</span>
                        ) : null}
                    </p>
                </div>
                {role === 'company' ? (
                    <button
                        type="button"
                        onClick={onCreateTask}
                        className="inline-flex shrink-0 items-center gap-2 rounded-xl bg-teal-700 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-teal-800 dark:bg-teal-600 dark:hover:bg-teal-500"
                    >
                        <i className="fa-solid fa-plus text-xs" />
                        Create task
                    </button>
                ) : (
                    <button
                        type="button"
                        onClick={() =>
                            openCompanyJobRoute({
                                section: 'workspace',
                                panel: 'tasks',
                                view: 'assigned',
                            })
                        }
                        className="inline-flex shrink-0 items-center gap-2 rounded-xl bg-teal-700 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-teal-800 dark:bg-teal-600 dark:hover:bg-teal-500"
                    >
                        View assigned tasks
                    </button>
                )}
            </div>

            {role === 'company' ? (
                <>
                    <SummaryPanel
                        title="Task overview"
                        subtitle={`${companyMetrics.activeEmployees} team members · ${companyMetrics.totalTasks} tasks total`}
                    >
                        <div className="grid grid-cols-2 gap-x-4 gap-y-5 sm:grid-cols-3 lg:grid-cols-6">
                            <StatCell label="Active" value={companyMetrics.activeTasks} />
                            <StatCell label="Pending review" value={companyMetrics.pendingReview} />
                            <StatCell label="Completed" value={companyMetrics.completedTasks} />
                            <StatCell
                                label="Disputed"
                                value={companyMetrics.disputedTasks}
                                hint={
                                    companyMetrics.disputedTasks > 0 ? 'Needs attention' : undefined
                                }
                            />
                            <StatCell
                                label="Refund requested"
                                value={companyMetrics.refundRequestedTasks}
                                hint={
                                    companyMetrics.refundRequestedTasks > 0
                                        ? 'Needs attention'
                                        : undefined
                                }
                            />
                            <StatCell label="Refunded" value={companyMetrics.refundedTasks} />
                        </div>
                    </SummaryPanel>

                    <EscrowFundDistribution
                        locked={companyMetrics.escrow.locked}
                        released={companyMetrics.escrow.released}
                        refunded={companyMetrics.escrow.refunded}
                        tokenSymbol={tokenSymbol}
                    />

                    <NeedsAttentionPanel
                        companyTasks={companyTasks}
                        employees={employees}
                        onOpenTask={onOpenTask}
                        onViewTasks={() =>
                            openCompanyJobRoute({
                                section: 'workspace',
                                panel: 'tasks',
                                view: 'company',
                            })
                        }
                    />
                </>
            ) : (
                <>
                    {payrollCard}

                    <SummaryPanel title="At a glance">
                        <div className="grid gap-6 lg:grid-cols-2">
                            <div>
                                <p className="mb-3 text-xs font-semibold text-zinc-700 dark:text-zinc-300">
                                    Payments
                                </p>
                                <div className="grid grid-cols-2 gap-4">
                                    <StatCell
                                        label="Locked"
                                        value={`${formatNumber(employeeMetrics.totalLocked)} ${tokenSymbol}`}
                                    />
                                    <StatCell
                                        label="Received"
                                        value={`${formatNumber(employeeMetrics.totalEarned)} ${tokenSymbol}`}
                                    />
                                    <StatCell
                                        label="Awaiting release"
                                        value={employeeMetrics.awaitingRelease}
                                        hint="Submitted, pending payout"
                                    />
                                    <StatCell
                                        label="Disputes"
                                        value={employeeMetrics.openDisputes}
                                    />
                                </div>
                            </div>
                            <div className="border-t border-zinc-100 pt-5 lg:border-l lg:border-t-0 lg:pl-6 lg:pt-0 dark:border-zinc-800">
                                <p className="mb-3 text-xs font-semibold text-zinc-700 dark:text-zinc-300">
                                    Tasks
                                </p>
                                <div className="grid grid-cols-2 gap-4">
                                    <StatCell
                                        label="Assigned"
                                        value={employeeMetrics.assignedCount}
                                    />
                                    <StatCell label="In progress" value={employeeMetrics.activeTasks} />
                                    <StatCell label="Completed" value={employeeMetrics.completedTasks} />
                                    <StatCell label="Under review" value={employeeMetrics.pendingTasks} />
                                </div>
                                <button
                                    type="button"
                                    onClick={() =>
                                        openCompanyJobRoute({
                                            section: 'workspace',
                                            panel: 'tasks',
                                            view: 'assigned',
                                        })
                                    }
                                    className="mt-4 text-xs font-semibold text-teal-700 hover:text-teal-800 dark:text-teal-400"
                                >
                                    View my tasks →
                                </button>
                            </div>
                        </div>
                    </SummaryPanel>

                    <RecentActivity
                        notifications={notifications}
                        onViewAll={() =>
                            openCompanyJobRoute({
                                section: 'workspace',
                                panel: 'activity',
                            })
                        }
                    />
                </>
            )}
        </div>
    );
};

export default JobsWorkspaceDashboard;
